import * as THREE from 'three';

interface Particle {
  position: THREE.Vector3;
  velocity: THREE.Vector3;
  life: number;
  maxLife: number;
  size: number;
  color: THREE.Color;
}

export class ParticleSystem {
  private particles: Particle[] = [];
  private geometry: THREE.BufferGeometry;
  private material: THREE.PointsMaterial;
  private points: THREE.Points;
  private maxParticles: number;

  constructor(parent: THREE.Object3D, maxParticles = 500) {
    this.maxParticles = maxParticles;

    this.geometry = new THREE.BufferGeometry();
    const positions = new Float32Array(maxParticles * 3);
    const colors = new Float32Array(maxParticles * 3);
    const sizes = new Float32Array(maxParticles);

    this.geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    this.geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
    this.geometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1));

    this.material = new THREE.PointsMaterial({
      size: 0.3,
      vertexColors: true,
      transparent: true,
      opacity: 0.8,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });

    this.points = new THREE.Points(this.geometry, this.material);
    parent.add(this.points);
  }

  emit(
    position: THREE.Vector3,
    count: number,
    color: THREE.Color = new THREE.Color(0xff6600),
    speed: number = 5,
    lifetime: number = 0.5
  ): void {
    for (let i = 0; i < count; i++) {
      if (this.particles.length >= this.maxParticles) break;

      this.particles.push({
        position: position.clone(),
        velocity: new THREE.Vector3(
          (Math.random() - 0.5) * speed,
          Math.random() * speed * 0.5,
          (Math.random() - 0.5) * speed
        ),
        life: lifetime,
        maxLife: lifetime,
        size: 0.1 + Math.random() * 0.3,
        color: color.clone(),
      });
    }
  }

  emitExplosion(position: THREE.Vector3): void {
    this.emit(position, 30, new THREE.Color(0xff4400), 8, 0.8);
    this.emit(position, 20, new THREE.Color(0xffaa00), 5, 1.0);
  }

  emitMuzzleFlash(position: THREE.Vector3, direction: THREE.Vector3): void {
    for (let i = 0; i < 5; i++) {
      if (this.particles.length >= this.maxParticles) break;

      const vel = direction.clone().multiplyScalar(15 + Math.random() * 10);
      vel.x += (Math.random() - 0.5) * 3;
      vel.y += (Math.random() - 0.5) * 3;

      this.particles.push({
        position: position.clone(),
        velocity: vel,
        life: 0.1,
        maxLife: 0.1,
        size: 0.05 + Math.random() * 0.1,
        color: new THREE.Color(0xffff88),
      });
    }
  }

  update(dt: number): void {
    const positions = this.geometry.attributes.position as THREE.BufferAttribute;
    const colors = this.geometry.attributes.color as THREE.BufferAttribute;
    const sizes = this.geometry.attributes.size as THREE.BufferAttribute;

    // Update alive particles
    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i];
      p.life -= dt;

      if (p.life <= 0) {
        this.particles.splice(i, 1);
        continue;
      }

      p.position.add(p.velocity.clone().multiplyScalar(dt));
      p.velocity.y -= 9.8 * dt; // gravity
    }

    // Write to buffers
    for (let i = 0; i < this.maxParticles; i++) {
      if (i < this.particles.length) {
        const p = this.particles[i];
        positions.setXYZ(i, p.position.x, p.position.y, p.position.z);
        const alpha = p.life / p.maxLife;
        colors.setXYZ(i, p.color.r * alpha, p.color.g * alpha, p.color.b * alpha);
        sizes.setX(i, p.size * alpha);
      } else {
        sizes.setX(i, 0); // hide unused
      }
    }

    positions.needsUpdate = true;
    colors.needsUpdate = true;
    sizes.needsUpdate = true;

    this.geometry.setDrawRange(0, this.particles.length);
  }

  dispose(): void {
    this.geometry.dispose();
    this.material.dispose();
  }
}
